import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePublicIntegration } from '@frontend/context/PublicIntegrationContext';
import {
  getServiceAuthPolicy,
  buildServiceLoginPath,
  isLaunchableServiceId,
} from '@frontend/config/authPolicyRegistry';

/**
 * Canonical service launch (ARCH-011). Redirects to the service OIDC/SSO entry URL.
 * If entry cannot be resolved, send the user to the shared service-login shell.
 */
export default function ServiceLaunchPage() {
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const integration = usePublicIntegration();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const policy = useMemo(
    () => (isLaunchableServiceId(serviceId) ? getServiceAuthPolicy(serviceId) : null),
    [serviceId]
  );

  const entryUrl = useMemo(() => {
    if (!policy) return null;
    return policy.resolveEntryUrl(origin, integration);
  }, [policy, origin, integration]);

  useEffect(() => {
    if (!policy) return;
    if (entryUrl) {
      window.location.replace(entryUrl);
      return;
    }
    window.location.replace(buildServiceLoginPath(serviceId, { switchUser: false }));
  }, [policy, entryUrl, serviceId]);

  if (!policy) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="card" style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
          <h1>Unknown service</h1>
          <p style={{ color: 'var(--gray-600)' }}>No launch policy for “{serviceId}”.</p>
          <Link to="/dashboard" className="btn btn-primary">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div className="card" style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
        <h1>Opening {policy.title}…</h1>
        <p style={{ color: 'var(--gray-600)' }}>Continuing with single sign-on.</p>
      </div>
    </div>
  );
}
